/**
 * Mapping from i18next language codes to date-fns locales.
 * Used for calendar and date formatting across the app.
 */
import {
    enUS,
    ru,
    de,
    fr,
    es,
    pl,
    it,
    pt,
    nl,
    sv,
    da,
    nb, // Norwegian uses 'nb' (Bokmål) in date-fns
    ar,
    bg,
    hr,
    cs,
    id,
    hu,
    tr,
    zhCN,
    uk,
    vi,
    fi,
    ptBR,
} from "date-fns/locale";
import type { Locale } from "date-fns";

/**
 * Map of i18n language codes to date-fns locale objects.
 */
const DATE_LOCALES: Record<string, Locale> = {
    en: enUS,
    ru: ru,
    de: de,
    fr: fr,
    es: es,
    pl: pl,
    it: it,
    pt: ptBR, // "/pt" is the Brazilian locale
    "pt-pt": pt, // European Portuguese
    nl: nl,
    sv: sv,
    da: da,
    no: nb, // Norwegian Bokmål
    ar: ar,
    bg: bg,
    hr: hr,
    cs: cs,
    id: id,
    hu: hu,
    tr: tr,
    zh: zhCN,
    uk: uk,
    vi: vi,
    fi: fi,
};

/**
 * Get the date-fns locale for a given i18n language code.
 * Falls back to English if the language is not supported.
 */
export function getDateLocale(langCode: string): Locale {
    // Region-tagged codes (pt-pt) must match before falling back to the bare
    // language, or "pt-pt" resolves to the Brazilian entry.
    const code = langCode.toLowerCase().replace(/_/g, "-");
    return DATE_LOCALES[code] || DATE_LOCALES[code.slice(0, 2)] || enUS;
}

/**
 * Get the Luxon locale string for a given i18n language code.
 * Luxon uses standard locale strings.
 */
export function getLuxonLocale(langCode: string): string {
    const code = langCode.toLowerCase().replace(/_/g, "-");
    // Luxon uses standard locale codes, map any special cases
    const luxonMap: Record<string, string> = {
        no: "nb", // Norwegian Bokmål
        zh: "zh-CN",
        pt: "pt-BR", // "/pt" is the Brazilian locale
        "pt-pt": "pt-PT",
    };
    return luxonMap[code] || luxonMap[code.slice(0, 2)] || code.slice(0, 2);
}
